import React, { useEffect, useState, useCallback } from "react";
import { Card, Badge, Nav } from "react-bootstrap";
import axiosInstance from "../services/axiosInterceptor";
import { getUserId } from "../services/authorize";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";

export default function IslamicAdvisorGrade() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearList, setYearList] = useState([]);
  const [room, setRoom] = useState(null);
  const [tab, setTab] = useState("scores");

  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [scoreMap, setScoreMap] = useState({});
  const [attRows, setAttRows] = useState([]);

  useEffect(() => {
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`)
      .then(res => {
        const list = res.data;
        setYearList(list);
        let id = localStorage.getItem("yearId");
        if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
        setYearId(id);
      })
      .catch(() => {});
  }, []);

  const loadGradeMatrix = useCallback((ir_id) => {
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-assessment/gradeMatrix/${ir_id}`)
      .then(res => {
        const rows = res.data;
        const subMap = {};
        rows.forEach(r => {
          subMap[r.isubject_id] = { isubject_id: r.isubject_id, isubject_isid: r.isubject_isid, isubject_name: r.isubject_name };
        });
        setSubjects(Object.values(subMap).sort((a, b) => a.isubject_isid.localeCompare(b.isubject_isid)));

        const stMap = {};
        rows.forEach(r => {
          stMap[r.is_study_id] = { is_study_id: r.is_study_id, st_id: r.st_id, st_idnumber: r.st_idnumber, st_name: r.st_name };
        });
        setStudents(Object.values(stMap).sort((a, b) => a.st_idnumber.localeCompare(b.st_idnumber)));

        const sm = {};
        rows.forEach(r => {
          if (!sm[r.is_study_id]) sm[r.is_study_id] = {};
          sm[r.is_study_id][r.isubject_id] = r.is_scoreVal;
        });
        setScoreMap(sm);
      })
      .catch(() => { setSubjects([]); setStudents([]); setScoreMap({}); });
  }, []);

  const loadAttendance = useCallback((ir_id) => {
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-assessment/attendanceSummary/${ir_id}`)
      .then(res => setAttRows(res.data))
      .catch(() => setAttRows([]));
  }, []);

  useEffect(() => {
    if (!yearId) return;
    const t_id = getUserId();
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-advisor/myRoom/${t_id}/${yearId}`)
      .then(res => {
        setRoom(res.data);
        loadGradeMatrix(res.data.ir_id);
        loadAttendance(res.data.ir_id);
      })
      .catch(() => { setRoom(null); setSubjects([]); setStudents([]); setAttRows([]); })
      .finally(() => setLoading(false));
  }, [yearId, setLoading, loadGradeMatrix, loadAttendance]);

  const average = (subjectId) => {
    const vals = students.map(s => scoreMap[s.is_study_id]?.[subjectId]).filter(v => v != null);
    if (vals.length === 0) return "-";
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  };

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="main-title mb-0">{t.advisorGrade.pageTitle}</h4>
          {room && (
            <p className="text-secondary mb-0 small">
              {t.common.classNo(room.ic_class, room.ir_no)}&nbsp;&nbsp;
              <Badge bg="info">{room.is_programCode}</Badge>
            </p>
          )}
        </div>
        <select
          className="form-select w-auto"
          value={yearId || ""}
          onChange={e => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }}
        >
          {yearList.map(y => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYear(y.year_year)}</option>
          ))}
        </select>
      </div>

      {!room ? (
        <Card className="card-one">
          <Card.Body className="text-center text-secondary py-5">
            <i className="ri-information-line fs-3 d-block mb-2"></i>
            {t.advisorGrade.noRoom}
          </Card.Body>
        </Card>
      ) : (
        <Card className="card-one">
          <Card.Header className="d-flex align-items-center gap-2">
            <span className="fw-semibold">{t.common.classNo(room.ic_class, room.ir_no)}</span>
            <Badge bg="info">{room.is_programCode}</Badge>
            <Badge bg={room.ir_gender === 'm' ? 'primary' : 'danger'}>{room.ir_gender === 'm' ? t.common.male : t.common.female}</Badge>
            <span className="ms-auto text-secondary small">{t.common.studentCount(room.student_count)}</span>
          </Card.Header>

          <Card.Header className="py-1 border-top-0">
            <Nav variant="tabs" className="card-header-tabs" activeKey={tab} onSelect={setTab}>
              <Nav.Item><Nav.Link eventKey="scores">{t.advisorGrade.tabScores}</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="attendance">{t.advisorGrade.tabAttendance}</Nav.Link></Nav.Item>
            </Nav>
          </Card.Header>

          <Card.Body className="p-0" style={{ overflowX: "auto" }}>
            {tab === "scores" && (
              subjects.length === 0 ? (
                <p className="text-center text-secondary py-4">{t.advisorGrade.noSubjects}</p>
              ) : (
                <table className="table table-bordered table-hover mb-0 small">
                  <thead className="table-secondary">
                    <tr>
                      <th rowSpan={2} style={{ minWidth: 30 }}>#</th>
                      <th rowSpan={2} style={{ minWidth: 90 }}>{t.advisorGrade.colNo}</th>
                      <th rowSpan={2} style={{ minWidth: 180 }}>{t.advisorGrade.colName}</th>
                      {subjects.map(sub => (
                        <th key={sub.isubject_id} className="text-center arabic-text" style={{ minWidth: 90 }}>
                          <div>{sub.isubject_isid}</div>
                          <div className="text-muted" style={{ fontSize: "0.75em" }}>{sub.isubject_name}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s, idx) => (
                      <tr key={s.is_study_id}>
                        <td className="text-secondary">{idx + 1}</td>
                        <td>{s.st_idnumber}</td>
                        <td>{s.st_name}</td>
                        {subjects.map(sub => {
                          const val = scoreMap[s.is_study_id]?.[sub.isubject_id];
                          return (
                            <td key={sub.isubject_id} className="text-center">
                              {val != null ? val : <span className="text-muted">-</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-secondary">
                    <tr>
                      <td colSpan={3} className="text-end fw-semibold small">{t.advisorGrade.colAverage}</td>
                      {subjects.map(sub => (
                        <td key={sub.isubject_id} className="text-center fw-semibold">{average(sub.isubject_id)}</td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              )
            )}

            {tab === "attendance" && (
              attRows.length === 0 ? (
                <p className="text-center text-secondary py-4">{t.advisorGrade.noAttendance}</p>
              ) : (
                <table className="table table-bordered table-hover mb-0 small">
                  <thead className="table-secondary">
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>{t.advisorGrade.colNo}</th>
                      <th>{t.advisorGrade.colName}</th>
                      <th className="text-center text-success">{t.advisorGrade.colPresent}</th>
                      <th className="text-center text-danger">{t.advisorGrade.colAbsent}</th>
                      <th className="text-center text-warning">{t.advisorGrade.colLate}</th>
                      <th className="text-center text-secondary">{t.advisorGrade.colLeave}</th>
                      <th className="text-center">{t.advisorGrade.colTotal}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attRows.map((r, idx) => (
                      <tr key={r.st_id}>
                        <td className="text-secondary">{idx + 1}</td>
                        <td>{r.st_idnumber}</td>
                        <td>{r.st_name}</td>
                        <td className="text-center text-success">{r.cnt_present}</td>
                        <td className="text-center text-danger">{r.cnt_absent}</td>
                        <td className="text-center text-warning">{r.cnt_late}</td>
                        <td className="text-center text-secondary">{r.cnt_leave}</td>
                        <td className="text-center">{r.cnt_total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </Card.Body>
        </Card>
      )}
    </React.Fragment>
  );
}
